import type { ReactNode } from "react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { Star, Heart, Video } from "lucide-react";
import { toast } from "@/hooks/use-toast";

interface TestimonialCardProps {
  name: string;
  initials: string;
  testimonial: string;
  rating: number;
  wouldRecommend?: boolean;
  submittedAt?: string;
  onClick?: () => void;
  videoUrl?: string;
  footerChart?: ReactNode;
}

const TestimonialCard = ({ name, initials, testimonial, rating, wouldRecommend, submittedAt, onClick, videoUrl, footerChart }: TestimonialCardProps) => {
  return (
    <Card className={`h-full ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`} onClick={onClick}>
      <CardContent className="pt-6">
        <div className="flex items-center gap-4 mb-3">
          <Avatar className="h-10 w-10 border-2 border-glyco-purple"><AvatarFallback className="bg-glyco-purple-light text-glyco-purple">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium">{name}</p>
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`h-3.5 w-3.5 ${
                    i < rating ? "text-glyco-yellow-dark fill-glyco-yellow-dark" : "text-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
        
        {submittedAt && (
          <p className="text-xs text-muted-foreground mb-3">{new Date(submittedAt).toLocaleDateString()}</p>
        )}
        
        {testimonial ? (
          <p className="text-sm text-muted-foreground mb-4">{testimonial}</p>
        ) : (
          <p className="text-sm text-muted-foreground mb-4 italic opacity-70">No textual review</p>
        )}
        
        {footerChart && (
          <div className="mb-4">{footerChart}</div>
        )}
        
        {wouldRecommend && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Heart className="h-3 w-3 text-green-600" />
            <span className="whitespace-nowrap">Would recommend product</span>
          </div>
        )}
        {videoUrl && (
          <div className="mt-3">
            <a
              href={videoUrl}
              target="_blank"
              rel="noopener noreferrer"
              download
              className="text-sm inline-flex items-center gap-1 text-glyco-purple hover:underline"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                toast({
                  title: "Video download coming soon",
                  description: "Video download is being developed, please wait another few days.",
                });
              }}
            >
              <Video className="h-3.5 w-3.5" />
              <span>Download video</span>
            </a>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TestimonialCard;
